import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getPlatformSpecs, calculateCropDimensions } from '../../constants/platformSpecs';
import styles from './ImageCropPreview.module.scss';

const ImageCropPreview = ({ imageUrl, platformType, onRemove }) => {
  const [imageDimensions, setImageDimensions] = useState(null);
  const [cropArea, setCropArea] = useState(null);
  const containerRef = useRef(null);

  const specs = getPlatformSpecs(platformType);

  useEffect(() => {
    if (!imageUrl) {
      setImageDimensions(null);
      setCropArea(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
      setImageDimensions(dimensions);

      if (specs) {
        const crop = calculateCropDimensions(dimensions.width, dimensions.height, platformType);
        setCropArea(crop);
      }
    };
    img.src = imageUrl;
  }, [imageUrl, platformType, specs]);

  if (!imageUrl) return null;

  const aspectRatio = specs?.imageSpecs?.aspectRatio || 1.91;

  // Calculate overlay mask for cropped areas
  const getCropOverlayStyle = () => {
    if (!imageDimensions || !cropArea) return {};

    const scaleX = 100 / imageDimensions.width;
    const scaleY = 100 / imageDimensions.height;

    return {
      '--crop-x': `${cropArea.x * scaleX}%`,
      '--crop-y': `${cropArea.y * scaleY}%`,
      '--crop-width': `${cropArea.width * scaleX}%`,
      '--crop-height': `${cropArea.height * scaleY}%`,
    };
  };

  return (
    <div className={styles.imagePreviewContainer} ref={containerRef}>
      <div className={styles.imageWrapper}>
        <img
          src={imageUrl}
          alt="Preview"
          className={styles.previewImage}
        />
      </div>

      {onRemove && (
        <button
          type="button"
          className={styles.removeButton}
          onClick={onRemove}
          aria-label="Remove image"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      )}
    </div>
  );
};

ImageCropPreview.propTypes = {
  imageUrl: PropTypes.string,
  platformType: PropTypes.oneOf(['linkedin', 'facebook', 'x', 'instagram']),
  onRemove: PropTypes.func,
};

export default ImageCropPreview;
